import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Fab, List, ListItem, ListItemIcon, ListItemText, TextField, Typography, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useEffect, useState } from "react";
import { OfferItem } from "./CustomerOfferDialog";
import { Autocomplete } from "@material-ui/lab";
import ArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import ArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import DeleteIcon from '@material-ui/icons/Delete';

interface Props {
  title: string,
  promptMessage: string;
  offerItems: OfferItem[];
  onOfferItemsSelected: (selectedOfferItems: OfferItem[]) => void
};

const useStyles = makeStyles(theme => ({
  button: {
    width: 180,
    height: theme.spacing(5),
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(0.5)
  },
  itemSelect: {
    width: 200,
    margin: theme.spacing(1)
  },
  selectionBox: {
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(2)
  }
}));

const useAccordionStyles = makeStyles(theme => ({
  root: {
    marginTop: 10,
    minWidth: 600
  }
}));

const getOfferItemDisplayedText = (offerItem: OfferItem) : string => {
  const name = offerItem.name;
  const material = offerItem.material;
  return material === undefined ? name : `${name} (${material})`;
};

const CustomerOfferItemsAccordion = (props: Props) => {
  const classes = useStyles();
  const accordionClasses = useAccordionStyles();
  const [selectedOfferItems, setSelectedOfferItems] = useState<OfferItem[]>([]);
  const [selectedOfferItem, setSelectedOfferItem] = useState<OfferItem|null>(null);
  const offerItems = props.offerItems.filter(offerItem => !selectedOfferItems.includes(offerItem));
  const offerItemSelectionDisabled = offerItems.length === 0;

  const updateSelectedOfferItems = (selectedOfferItems: OfferItem[]) => {
    setSelectedOfferItems(selectedOfferItems);
    props.onOfferItemsSelected(selectedOfferItems);
  };

  const handleSelectedOfferItemChange = (offerItem: OfferItem|null) => {
    setSelectedOfferItem(offerItem);
  };

  const handlePrependTop = () => {
    if (selectedOfferItem !== null && !selectedOfferItems.includes(selectedOfferItem)) {
      updateSelectedOfferItems([selectedOfferItem].concat(selectedOfferItems));
    }
  };

  const handleAppendBottom = () => {
    if (selectedOfferItem !== null && !selectedOfferItems.includes(selectedOfferItem)) {
      updateSelectedOfferItems(selectedOfferItems.concat(selectedOfferItem));
    }
  };

  const handleMoveOfferItemUp = (offerItem: OfferItem) =>  {
    const indexOfMovedOfferItem = selectedOfferItems.findIndex(selectedOfferItem => selectedOfferItem === offerItem);
    const selectedOfferItemsBeforePreviousToMovedOfferItem = selectedOfferItems.slice(0, indexOfMovedOfferItem - 1);
    const offerItemBeforeMovedOfferItem = selectedOfferItems[indexOfMovedOfferItem - 1];
    const remainingSelectedOfferItems = selectedOfferItems.slice(indexOfMovedOfferItem + 1);
    updateSelectedOfferItems(
      selectedOfferItemsBeforePreviousToMovedOfferItem
        .concat(offerItem)
        .concat(offerItemBeforeMovedOfferItem)
        .concat(remainingSelectedOfferItems));
  };

  const handleMoveOfferItemDown = (offerItem: OfferItem) =>  {
    const indexOfMovedOfferItem = selectedOfferItems.findIndex(selectedOfferItem => selectedOfferItem === offerItem);
    const selectedOfferItemsBeforeMovedOfferItem = selectedOfferItems.slice(0, indexOfMovedOfferItem);
    const offerItemAfterMovedOfferItem = selectedOfferItems[indexOfMovedOfferItem + 1];
    const remainingSelectedOfferItems = selectedOfferItems.slice(indexOfMovedOfferItem + 2);
    updateSelectedOfferItems(
      selectedOfferItemsBeforeMovedOfferItem
        .concat(offerItemAfterMovedOfferItem)
        .concat(offerItem)
        .concat(remainingSelectedOfferItems));
  };

  const isOfferItemUpButtonDisabled = (offerItem: OfferItem) : boolean => {
    const numberOfSelectedOfferItems = selectedOfferItems.length;
    if (numberOfSelectedOfferItems > 1) {
      const firstSelectedOfferItem = selectedOfferItems[0];
      return firstSelectedOfferItem === offerItem;
    } else {
      return true;
    }
  };

  const isOfferItemDownButtonDisabled = (offerItem: OfferItem) : boolean => {
    const numberOfSelectedOfferItems = selectedOfferItems.length;
    if (numberOfSelectedOfferItems > 1) {
      const lastSelectedOfferItem = selectedOfferItems[numberOfSelectedOfferItems - 1];
      return lastSelectedOfferItem === offerItem;
    } else {
      return true;
    }
  };

  const handleDeleteOrderItem = (offerItem: OfferItem) => {
    const indexOfDeletedOfferItem = selectedOfferItems.findIndex(selectedOfferItem => selectedOfferItem === offerItem);
    const selectedOfferItemsBeforeDeletedOfferItem = selectedOfferItems.slice(0, indexOfDeletedOfferItem);
    const selectedOfferItemsAfterDeletedOfferItem = selectedOfferItems.slice(indexOfDeletedOfferItem + 1);
    updateSelectedOfferItems(
      selectedOfferItemsBeforeDeletedOfferItem
        .concat(selectedOfferItemsAfterDeletedOfferItem))
  };

  const selectAllOfferItems = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    updateSelectedOfferItems(props.offerItems);
  };

  const renderOfferItemSelectionBox = () : JSX.Element =>
    <Box display='flex' flexDirection='row'>
      <Autocomplete
          onChange={ (event, offerItem) => handleSelectedOfferItemChange(offerItem) }
          className={ classes.itemSelect }
          options={ offerItems || [] }
          getOptionLabel={ getOfferItemDisplayedText }
          renderInput={ (params) => <TextField {...params} /> }
          disabled={ offerItemSelectionDisabled }
      />
      <Button
          className={ classes.button }
          color='primary'
          variant='contained'
          onClick={ handlePrependTop }
          disabled={ offerItemSelectionDisabled }
      >
        Dodaj na początek
      </Button>
      <Button
          className={ classes.button }
          color='primary'
          variant='contained'
          onClick={ handleAppendBottom }
          disabled={ offerItemSelectionDisabled }
      >
        Dodaj na koniec
      </Button>
    </Box>
  ;

  const renderOfferItem = (offerItem: OfferItem) : JSX.Element =>
    <ListItem>
      <ListItemText>{ getOfferItemDisplayedText(offerItem) }</ListItemText>
      <ListItemIcon>
        <Fab
          aria-label='up'
          color='primary'
          onClick={ () => handleMoveOfferItemUp(offerItem) }
          size='small'
          disabled={ isOfferItemUpButtonDisabled(offerItem) }
        >
          <ArrowUpIcon />
        </Fab>
      </ListItemIcon>
      <ListItemIcon>
        <Fab
          aria-label='down'
          color='primary'
          onClick={ () => handleMoveOfferItemDown(offerItem) }
          size='small'
          disabled={ isOfferItemDownButtonDisabled(offerItem) }
        >
          <ArrowDownIcon />
        </Fab>
      </ListItemIcon>
      <ListItemIcon>
        <Fab
          aria-label='delete'
          color='secondary'
          onClick={ () => handleDeleteOrderItem(offerItem) }
          size='small'
        >
          <DeleteIcon />
        </Fab>
      </ListItemIcon>
    </ListItem>;

  const renderSelectedOfferItems = () : JSX.Element =>
    <List>
      { selectedOfferItems.map(renderOfferItem) }
    </List>;

  useEffect(() => {
    setSelectedOfferItems([]);
  }, [props.offerItems]);

  return (
    <Accordion classes={ accordionClasses }>
      <AccordionSummary expandIcon={ <ExpandMoreIcon /> } >
        <Box display='flex' flexDirection='row'>
          <Typography className={ classes.title }>
            { props.title }
          </Typography>
          <Button
              className={ classes.button }
              color='primary'
              variant='contained'
              onClick={ selectAllOfferItems }
              disabled={ selectedOfferItems.length === props.offerItems.length }
          >
            Wybierz wszystkie
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box className={ classes.selectionBox } >
          { props.promptMessage }
          { renderOfferItemSelectionBox() }
          { renderSelectedOfferItems() }
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default CustomerOfferItemsAccordion;